<template>
  <v-container fluid class="analysis-groups-page">
    <div class="analysis-groups-page__header">
      <div class="analysis-groups-page__title">
        <v-icon left color="primary">mdi-folder-multiple-outline</v-icon>
        <span class="headline">Analysis groups</span>
        <v-chip v-if="!loading" small outlined class="ml-3">{{ groups.length }}</v-chip>
        <v-spacer />
        <v-text-field
          v-model="search"
          placeholder="Search groups"
          prepend-inner-icon="mdi-magnify"
          dense
          outlined
          hide-details
          clearable
          class="analysis-groups-page__search mr-3"
        />
        <v-btn small outlined class="mr-2" :loading="loading" @click="load">
          <v-icon small left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn
          small
          color="primary"
          depressed
          class="analysis-groups-page__create"
          @click="openCreate"
        >
          <v-icon small left>mdi-plus</v-icon>
          New group
        </v-btn>
      </div>
      <div class="caption grey--text text--darken-1 analysis-groups-page__subtitle">
        Named experiments: one configuration version and one set of engines applied to N videos. A
        video can belong to several groups and be analysed by all of them in parallel.
      </div>
    </div>

    <v-alert v-if="error" type="error" outlined class="analysis-groups-page__error">
      <div class="d-flex align-center">
        <span>{{ error }}</span>
        <v-spacer />
        <v-btn small outlined color="error" @click="load">
          <v-icon small left>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </div>
    </v-alert>

    <v-skeleton-loader v-else-if="loading && !groups.length" type="table-row@5" class="mt-4" />

    <v-card v-else-if="!groups.length" outlined class="analysis-groups-page__empty">
      <v-icon size="48" color="grey lighten-1">mdi-folder-plus-outline</v-icon>
      <div class="subtitle-1 grey--text text--darken-1 mt-2">No groups yet</div>
      <div class="caption grey--text mb-3">
        Create a group to run one configuration over a set of videos and track its metrics.
      </div>
      <v-btn small color="primary" depressed @click="openCreate">
        <v-icon small left>mdi-plus</v-icon>
        Create group
      </v-btn>
    </v-card>

    <v-card v-else outlined class="analysis-groups-page__card">
      <v-data-table
        :headers="headers"
        :items="rows"
        :search="search"
        item-key="id"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        class="analysis-groups-page__table"
        @click:row="openGroup"
      >
        <template #[`item.name`]="{ item }">
          <div class="analysis-groups-page__name">
            <strong>{{ item.name }}</strong>
            <v-icon v-if="item.frozen_at" x-small class="ml-1" title="Frozen">mdi-snowflake</v-icon>
          </div>
          <div v-if="item.description" class="caption grey--text analysis-groups-page__description">
            {{ item.description }}
          </div>
        </template>
        <template #[`item.config`]="{ item }">
          <v-chip x-small outlined label class="analysis-groups-page__config">
            {{ item.config_label }}
          </v-chip>
        </template>
        <template #[`item.engines`]="{ item }">
          <v-chip
            v-for="engine in item.engines"
            :key="engine"
            x-small
            outlined
            label
            class="mr-1 mb-1"
          >
            {{ engine }}
          </v-chip>
        </template>
        <template #[`item.members`]="{ item }">
          {{ item.members }}
        </template>
        <template #[`item.last_batch`]="{ item }">
          <div v-if="item.last_batch" class="analysis-groups-page__batch">
            <span class="caption">{{ item.last_batch }}</span>
            <div class="d-flex flex-wrap">
              <v-chip
                v-for="count in item.status_counts"
                :key="count.status"
                x-small
                label
                :color="count.color"
                dark
                class="mr-1 mt-1"
              >
                {{ count.label }} {{ count.n }}
              </v-chip>
            </div>
          </div>
          <span v-else class="grey--text">—</span>
        </template>
        <template #[`item.metrics`]="{ item }">
          <div v-if="item.last_snapshot" class="analysis-groups-page__metrics">
            <span
              v-for="metric in metrics"
              :key="metric.key"
              class="analysis-groups-page__metric"
              :class="`${item.metric_colors[metric.key]}--text`"
              :title="metric.label"
            >
              {{ formatPct(item.metric_values[metric.key]) }}
            </span>
            <div class="caption grey--text">{{ item.last_snapshot_at }}</div>
          </div>
          <span v-else class="grey--text">No snapshot</span>
        </template>
        <template #[`item.actions`]="{ item }">
          <div class="d-flex align-center justify-end text-no-wrap">
            <v-btn icon small title="Open" :to="`/analysis-groups/${item.id}`" @click.stop>
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              title="Re-run group"
              class="analysis-groups-page__rerun"
              @click.stop="openRun(item)"
            >
              <v-icon small>mdi-play</v-icon>
            </v-btn>
            <v-btn icon small title="Add videos" @click.stop="openAdd(item)">
              <v-icon small>mdi-video-plus-outline</v-icon>
            </v-btn>
            <v-btn icon small title="Save snapshot" @click.stop="openSnapshot(item)">
              <v-icon small>mdi-camera-plus-outline</v-icon>
            </v-btn>
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="openEdit(item)">
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-list-item :to="`/coherence-evaluation?group=${item.id}`">
                  <v-list-item-title>Open in evaluation</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="!item.frozen_at" @click="freeze(item)">
                  <v-list-item-title>Freeze</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item class="analysis-groups-page__delete" @click="confirmDelete(item)">
                  <v-list-item-title class="error--text">Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <AnalysisGroupDialog
      v-model="dialog.open"
      :group="dialog.group"
      :analysis-types="analysisTypes"
      :loading-analysis-types="loadingAnalysisTypes"
      @saved="onSaved"
    />
    <AddToGroupDialog
      v-model="addDialog.open"
      :items="[]"
      :groups="groups"
      :locked-group-id="addDialog.groupId"
      @added="onAdded"
    />
    <RunGroupDialog v-model="runDialog.open" :group="runDialog.group" @launched="onLaunched" />
    <SaveSnapshotDialog
      v-model="snapshotDialog.open"
      :group-id="snapshotDialog.group ? snapshotDialog.group.id : null"
      :group-name="snapshotDialog.group ? snapshotDialog.group.name : ''"
      :saving="snapshotDialog.saving"
      :error="snapshotDialog.error"
      @save="saveSnapshot"
    />

    <v-dialog v-model="deleteDialog.open" max-width="440">
      <v-card>
        <v-card-title class="px-6">
          <h5 class="primary-color mb-0">Delete group</h5>
        </v-card-title>
        <v-card-text class="px-6">
          Delete <strong>{{ deleteDialog.group ? deleteDialog.group.name : '' }}</strong
          >? Its runs and snapshots are kept; only the group and its memberships are removed. Groups
          with queued or running runs cannot be deleted.
          <v-alert v-if="deleteDialog.error" dense text type="error" class="mt-3 mb-0">
            {{ deleteDialog.error }}
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-6 py-3">
          <v-spacer />
          <v-btn
            text
            class="cancel-modal"
            :disabled="deleteDialog.busy"
            @click="deleteDialog.open = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            depressed
            :loading="deleteDialog.busy"
            class="analysis-groups-page__confirm-delete"
            @click="remove"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.visible" :timeout="6000" :color="snackbar.color" bottom>
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import SaveSnapshotDialog from '@/components/evaluation/SaveSnapshotDialog.vue';
import AddToGroupDialog from '@/components/groups/AddToGroupDialog.vue';
import AnalysisGroupDialog from '@/components/groups/AnalysisGroupDialog.vue';
import RunGroupDialog from '@/components/groups/RunGroupDialog.vue';
import AnalysisGroupsService from '@/services/AnalysisGroupsService';
import InterviewsDataService from '@/services/InterviewsDataService';
import {
  SERIES_METRICS,
  metricStatus,
  metricStatusColor,
  metricValue,
} from '@/utils/metricsSeries';
import { runStatusPresentation } from '@/utils/runLabels';

/**
 * /analysis-groups (M4 screen, behind `analysisGroups`): every group with its configuration,
 * engines, members, last batch status and last snapshot metrics; create / edit, re-run, add
 * videos, snapshot and delete.
 */
export default {
  name: 'AnalysisGroupsList',

  components: {
    AddToGroupDialog,
    AnalysisGroupDialog,
    RunGroupDialog,
    SaveSnapshotDialog,
  },

  data() {
    return {
      groups: [],
      loading: false,
      error: '',
      search: '',
      metrics: SERIES_METRICS,
      analysisTypes: [],
      loadingAnalysisTypes: false,
      dialog: { open: false, group: null },
      addDialog: { open: false, groupId: null },
      runDialog: { open: false, group: null },
      snapshotDialog: { open: false, group: null, saving: false, error: '' },
      deleteDialog: { open: false, group: null, busy: false, error: '' },
      snackbar: { visible: false, text: '', color: 'success' },
    };
  },

  computed: {
    headers() {
      return [
        { text: 'Group', value: 'name' },
        { text: 'Config', value: 'config', sortable: false, width: 110 },
        { text: 'Engines', value: 'engines', sortable: false },
        { text: 'Videos', value: 'members', width: 90, align: 'end' },
        { text: 'Last batch', value: 'last_batch', sortable: false },
        { text: 'Last snapshot', value: 'metrics', sortable: false, width: 190 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 220 },
      ];
    },
    rows() {
      return this.groups.map((group) => {
        const snapshot = group.last_snapshot || null;
        const runsByStatus = group.runs_by_status || group.counts?.runs_by_status || {};
        const values = {};
        const colors = {};
        SERIES_METRICS.forEach((metric) => {
          values[metric.key] = snapshot ? metricValue(snapshot, metric.key) : null;
          colors[metric.key] = metricStatusColor(metricStatus(metric.key, values[metric.key]));
        });
        return {
          ...group,
          id: Number(group.id),
          members: Number(group.members ?? group.counts?.members ?? 0),
          engines: Array.isArray(group.engines) ? group.engines : [],
          config_label: group.config_version
            ? `config v${group.config_version}`
            : 'config: current',
          last_batch: group.last_batch || null,
          status_counts: Object.keys(runsByStatus)
            .filter((status) => Number(runsByStatus[status]) > 0)
            .map((status) => ({
              status,
              n: Number(runsByStatus[status]),
              label: runStatusPresentation(status).label,
              color: runStatusPresentation(status).color,
            })),
          last_snapshot: snapshot,
          last_snapshot_at:
            snapshot && snapshot.computed_at ? this.formatDate(snapshot.computed_at) : '',
          metric_values: values,
          metric_colors: colors,
        };
      });
    },
  },

  created() {
    this.load();
    this.loadAnalysisTypes();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const response = await AnalysisGroupsService.list({ limit: 200 });
        const body = response?.data?.data ?? response?.data ?? {};
        this.groups = Array.isArray(body.items) ? body.items : Array.isArray(body) ? body : [];
      } catch (error) {
        this.error = 'Failed to load the analysis groups.';
      } finally {
        this.loading = false;
      }
    },

    async loadAnalysisTypes() {
      this.loadingAnalysisTypes = true;
      try {
        const response = await InterviewsDataService.findAllInterviewAnalysisTypes(1, 100);
        const items = response?.data?.data;
        this.analysisTypes = Array.isArray(items) ? items : [];
      } catch (error) {
        this.analysisTypes = [];
      } finally {
        this.loadingAnalysisTypes = false;
      }
    },

    formatPct(value) {
      return value === null || value === undefined ? '—' : `${value}%`;
    },

    formatDate(value) {
      const date = new Date(value);
      if (!Number.isFinite(date.getTime())) return String(value);
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}`;
    },

    notify(text, color = 'success') {
      this.snackbar = { visible: true, text, color };
    },

    openGroup(row) {
      this.$router.push(`/analysis-groups/${row.id}`);
    },

    openCreate() {
      this.dialog = { open: true, group: null };
    },

    openEdit(group) {
      this.dialog = { open: true, group };
    },

    openAdd(group) {
      this.addDialog = { open: true, groupId: group.id };
    },

    openRun(group) {
      this.runDialog = { open: true, group };
    },

    openSnapshot(group) {
      this.snapshotDialog = { open: true, group, saving: false, error: '' };
    },

    onSaved(group) {
      this.notify(`Group ${group && group.name ? group.name : ''} saved`.trim());
      this.load();
    },

    onAdded(result) {
      const added = result.added != null ? `${result.added} added` : 'videos added';
      const skipped = result.skipped ? ` · ${result.skipped} already in the group` : '';
      this.notify(`${added}${skipped}`);
      this.load();
    },

    onLaunched(result) {
      const n = result.accepted.length;
      const parts = [`Queued ${n} ${n === 1 ? 'run' : 'runs'}`];
      if (result.batch) parts.push(`batch ${result.batch}`);
      if (result.skipped.length) parts.push(`${result.skipped.length} skipped`);
      this.notify(parts.join(' · '), result.skipped.length && !n ? 'warning' : 'success');
      this.load();
    },

    async saveSnapshot({ note } = {}) {
      const group = this.snapshotDialog.group;
      if (!group) return;
      this.snapshotDialog.saving = true;
      this.snapshotDialog.error = '';
      try {
        await AnalysisGroupsService.createSnapshot(group.id, { note });
        this.snapshotDialog.open = false;
        this.notify('Snapshot saved');
        this.load();
      } catch (error) {
        const data = error?.response?.data;
        this.snapshotDialog.error =
          data?.message || data?.error || error?.message || 'Could not save the snapshot.';
      } finally {
        this.snapshotDialog.saving = false;
      }
    },

    async freeze(group) {
      try {
        await AnalysisGroupsService.freeze(group.id);
        this.notify(`Group ${group.name} frozen`);
        this.load();
      } catch (error) {
        this.notify('Could not freeze the group.', 'error');
      }
    },

    confirmDelete(group) {
      this.deleteDialog = { open: true, group, busy: false, error: '' };
    },

    async remove() {
      const group = this.deleteDialog.group;
      if (!group) return;
      this.deleteDialog.busy = true;
      this.deleteDialog.error = '';
      try {
        await AnalysisGroupsService.remove(group.id);
        this.deleteDialog.open = false;
        this.notify(`Group ${group.name} deleted`);
        this.load();
      } catch (error) {
        const status = error?.response?.status;
        this.deleteDialog.error =
          status === 409
            ? 'The group still has queued or running runs. Wait for them to finish or cancel them first.'
            : error?.response?.data?.message || 'Could not delete the group.';
      } finally {
        this.deleteDialog.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.analysis-groups-page {
  padding-top: 72px;
  max-width: 1400px;
}
.analysis-groups-page__header {
  margin-bottom: 16px;
}
.analysis-groups-page__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.analysis-groups-page__search {
  max-width: 260px;
}
.analysis-groups-page__subtitle {
  margin-top: 4px;
}
.analysis-groups-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 16px;
  text-align: center;
  border-radius: 10px !important;
}
.analysis-groups-page__card {
  border-radius: 10px !important;
}
.analysis-groups-page__table tbody tr {
  cursor: pointer;
}
.analysis-groups-page__description {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.analysis-groups-page__metrics {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}
.analysis-groups-page__metric {
  font-weight: 700;
}
</style>
